import React, {useEffect} from 'react';
import {pollForDiscovery, startDiscovery} from 'externalAPIs/insurance-eligibility';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import Analytics from 'utils/Analytics';
import styles from './SleepioRxInterstitials.module.css';

/**
 * Sohar insurance-discovery loading step (RET-2560).
 *
 * Runs after contact_info has created the person_id + eligibility token. Kicks off
 * a Sohar discovery from the demographics we already collected, polls for a result
 * (1.5s / 12s budget), and:
 *   - on a resolved match: pre-fills `member_id` + records the discovered `payerKey`
 *     as `discovered_payer_name`, sets `discovery_resolved = true` so the manual
 *     member-ID page is skipped, then advances.
 *   - otherwise (no match / timeout / error / missing inputs): sets
 *     `discovery_resolved = false` and advances to the manual member-ID page.
 *
 * Mirrors the nyx MH-screen discovery decision point.
 */
export const SleepioDiscoveryInterstitial: React.FC = () => {
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const finish = (resolved: boolean) => {
      if (cancelled) {
        return;
      }
      surveyInstance.setValue('discovery_resolved', resolved);
      SurveyData.save(surveyInstance);
      surveyInstance.nextPage();
    };

    const run = async () => {
      const data = SurveyData.read() ?? surveyInstance.data;
      const personId = Number(data.person_id);
      const token = data.eligibility_token;

      // Can't run discovery without identity + the inputs Sohar needs — fall back
      // to manual member-ID entry.
      if (!personId || !token || !data.dob || !data.first_name || !data.last_name || !data.insurance_state) {
        finish(false);
        return;
      }

      try {
        Analytics.track('SleepioRx Discovery Started');
        await startDiscovery(
          {
            personId,
            firstName: data.first_name,
            lastName: data.last_name,
            dateOfBirth: data.dob,
            state: data.insurance_state,
            phone: data.phone,
            email: data.email,
          },
          token,
        );

        const result = await pollForDiscovery(personId, token, controller.signal);
        const resolved = result.status === 'complete' && Boolean(result.found) && Boolean(result.memberId);

        if (resolved) {
          surveyInstance.setValue('member_id', result.memberId);
          // Use Sohar's payerKey as the intake payerName — it shares our namespace
          // for common payers but resolves edge ones the picker can't.
          if (result.payerKey) {
            surveyInstance.setValue('discovered_payer_name', result.payerKey);
          }
          Analytics.track('SleepioRx Discovery Resolved', {payer_key: result.payerKey ?? null});
        } else {
          Analytics.track('SleepioRx Discovery Manual Fallback Shown', {status: result.status ?? 'unknown'});
        }
        finish(resolved);
      } catch (error) {
        Analytics.track('SleepioRx Discovery Manual Fallback Shown', {status: 'error'});
        finish(false);
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`${styles.container} ${styles.centered}`}>
      <div className={styles.heroCard}>
        <div className={styles.kicker}>Checking coverage</div>
        <h1 className={styles.title}>Looking up your insurance…</h1>
        <p className={styles.body}>
          We're checking your plan so we can pre-fill your coverage details. This only takes a moment.
        </p>
      </div>
    </div>
  );
};

export default SleepioDiscoveryInterstitial;
